import type { AnalyticsApiKeyItem } from "@server/types/responseTypes";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  errorRateVariant,
  formatAudioHours,
  formatDuration,
  formatRequests,
} from "../utils/formatMetric";

function formatLastUsed({ iso }: { iso: string | null | undefined }): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface ApiKeyUsageTableProps {
  data: (AnalyticsApiKeyItem & { latencySamples?: number })[] | undefined;
  isLoading: boolean;
  isError?: boolean;
}

/** Displays per-key usage; absent response-time samples stay unavailable. */
export function ApiKeyUsageTable({
  data,
  isLoading,
  isError,
}: ApiKeyUsageTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[17px]">By API Key</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Key</TableHead>
              <TableHead className="text-right">Requests</TableHead>
              <TableHead className="text-right">Error Rate</TableHead>
              <TableHead className="text-right">Avg Latency</TableHead>
              <TableHead className="text-right">Audio</TableHead>
              <TableHead className="text-right pr-4">Last Used</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6} className="pl-4 pr-4">
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={6} className="pl-4 pr-4">
                  <div
                    role="alert"
                    className="text-center py-8 text-muted-foreground text-sm"
                  >
                    Failed to load API key data. Please try again.
                  </div>
                </TableCell>
              </TableRow>
            ) : !data || data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="px-4 py-6 text-center text-[13px] text-muted-foreground"
                >
                  No data for this period
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.apiKeyId}>
                  <TableCell className="pl-4">
                    <div className="font-medium text-[13px]">
                      {row.apiKeyName}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {row.keyPreview}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatRequests({ n: row.requests })}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={errorRateVariant({ rate: row.errorRate })}>
                      {row.errorRate.toFixed(1)}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatDuration({
                      ms:
                        row.latencySamples === 0 ? undefined : row.avgLatencyMs,
                    })}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatAudioHours({
                      seconds: row.audioSecondsProcessed ?? 0,
                    })}
                  </TableCell>
                  <TableCell className="text-right pr-4 text-muted-foreground text-xs">
                    {formatLastUsed({ iso: row.lastUsedAt })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
